import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import { BASE_URL } from '../config';
import type { Risorsa, RisorsaInput } from '../types';
import ResourceSkillsManager from './ResourceSkillsManager';
import AvatarUpload from './AvatarUpload';

interface FiguraProfessionale {
  id: number;
  nome: string;
  costo_giornaliero: number;
  costo_orario: number;
  enabled: boolean;
}

interface ResourceFormProps {
  open: boolean;
  risorsa: Risorsa | null;
  onClose: () => void;
  onSave: (risorsa: RisorsaInput, id?: number) => void;
}

const ResourceForm: React.FC<ResourceFormProps> = ({ open, risorsa, onClose, onSave }) => {
  const [formData, setFormData] = useState<RisorsaInput>({
    nome: '',
    cognome: '',
    email: '',
    struttura: '',
    costo_orario: 0,
    id_figura_professionale: null,
    foto: null,
    id_hrp: null,
    id_erp: null,
  });

  const [figureProfessionali, setFigureProfessionali] = useState<FiguraProfessionale[]>([]);
  const [errors, setErrors] = useState<{ nome?: string; costo_orario?: string }>({});
  const [activeTab, setActiveTab] = useState<number>(0);

  useEffect(() => {
    const loadFigureProfessionali = async () => {
      try {
        const userStr = sessionStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : null;

        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };
        if (user?.id) {
          headers['x-user-id'] = user.id.toString();
        }

        const response = await fetch(`${BASE_URL}/figure-professionali`, { headers });
        if (response.ok) {
          const data = await response.json();
          setFigureProfessionali(Array.isArray(data) ? data.filter((f: FiguraProfessionale) => f.enabled) : []);
        }
      } catch (error) {
        console.error('Error loading figure professionali:', error);
      }
    };

    if (open) {
      loadFigureProfessionali();
    }

    if (risorsa) {
      setFormData({
        nome: risorsa.nome || '',
        cognome: risorsa.cognome || '',
        email: risorsa.email || '',
        struttura: risorsa.struttura || '',
        costo_orario: risorsa.costo_orario || 0,
        id_figura_professionale: risorsa.id_figura_professionale || null,
        foto: risorsa.foto || null,
        id_hrp: risorsa.id_hrp || null,
        id_erp: risorsa.id_erp || null,
      });
    } else {
      setFormData({
        nome: '',
        cognome: '',
        email: '',
        struttura: '',
        costo_orario: 0,
        id_figura_professionale: null,
        foto: null,
        id_hrp: null,
        id_erp: null,
      });
    }
    setErrors({});
  }, [risorsa, open]);

  const handleChange = (field: keyof RisorsaInput) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({ ...formData, [field]: e.target.value });
    if (errors[field as keyof typeof errors]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  const handleSubmit = () => {
    // Validazione
    const newErrors: { nome?: string; costo_orario?: string } = {};
    if (!formData.nome || formData.nome.trim() === '') {
      newErrors.nome = 'Il nome è obbligatorio';
    }
    if (!formData.costo_orario || formData.costo_orario <= 0) {
      newErrors.costo_orario = 'Il costo orario deve essere maggiore di 0';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Chiama onSave
    onSave(formData, risorsa?.id);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{risorsa ? 'Modifica Risorsa' : 'Nuova Risorsa'}</DialogTitle>
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tab label="Informazioni Base" />
        <Tab label="Skills" disabled={!risorsa} />
      </Tabs>
      <DialogContent>
        {activeTab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Avatar Upload */}
          <AvatarUpload
            value={formData.foto}
            onChange={(foto) => setFormData({ ...formData, foto })}
          />

          <TextField
            label="Nome *"
            value={formData.nome}
            onChange={handleChange('nome')}
            error={!!errors.nome}
            helperText={errors.nome}
            fullWidth
            autoFocus
          />
          <TextField
            label="Cognome"
            value={formData.cognome}
            onChange={handleChange('cognome')}
            fullWidth
          />
          <TextField
            label="Email"
            value={formData.email}
            onChange={handleChange('email')}
            type="email"
            fullWidth
          />
          <TextField
            label="Struttura"
            value={formData.struttura}
            onChange={handleChange('struttura')}
            fullWidth
          />
          <TextField
            label="ID HRP"
            value={formData.id_hrp || ''}
            onChange={(e) => setFormData({ ...formData, id_hrp: e.target.value ? parseInt(e.target.value) : null })}
            type="number"
            fullWidth
            helperText="ID risorsa nel sistema HR/Payroll"
          />
          <TextField
            label="ID ERP"
            value={formData.id_erp || ''}
            onChange={(e) => setFormData({ ...formData, id_erp: e.target.value ? parseInt(e.target.value) : null })}
            type="number"
            fullWidth
            helperText="ID risorsa nel sistema ERP"
          />
          <FormControl fullWidth>
            <InputLabel>Figura Professionale</InputLabel>
            <Select
              value={formData.id_figura_professionale || ''}
              label="Figura Professionale"
              onChange={(e) => {
                const selectedId = e.target.value as number | '';
                const selectedFigura = figureProfessionali.find(f => f.id === selectedId);
                setFormData({
                  ...formData,
                  id_figura_professionale: selectedId === '' ? null : selectedId,
                  costo_orario: selectedFigura ? selectedFigura.costo_orario : formData.costo_orario,
                });
              }}
            >
              <MenuItem value="">
                <em>Nessuna</em>
              </MenuItem>
              {figureProfessionali.map((figura) => (
                <MenuItem key={figura.id} value={figura.id}>
                  {figura.nome} - €{figura.costo_orario.toFixed(2)}/h
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Costo Orario (€) *"
            value={formData.costo_orario}
            onChange={(e) => {
              const value = parseFloat(e.target.value) || 0;
              setFormData({ ...formData, costo_orario: value });
              if (errors.costo_orario) {
                setErrors({ ...errors, costo_orario: undefined });
              }
            }}
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            error={!!errors.costo_orario}
            helperText={errors.costo_orario || (formData.id_figura_professionale ? 'Costo derivato dalla figura professionale selezionata' : '')}
            fullWidth
          />
          </Box>
        )}
        {activeTab === 1 && (
          <Box sx={{ mt: 2 }}>
            <ResourceSkillsManager idRisorsa={risorsa?.id} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button onClick={handleSubmit} variant="contained">
          Salva
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResourceForm;
